import { useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { SecurityQuestions } from "./SecurityQuestions";
import { toast } from "sonner";
import { validatePassword, getPasswordStrength } from "./PasswordValidation";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { checkPasswordBreached } from "@/utils/passwordSecurity";
import { getDeviceFingerprint } from "@/utils/deviceInfo";
import { checkRateLimit } from "@/utils/securityMonitoring";
import { getStoredReferralCode, validateReferralCode } from "@/utils/AffiliateUtils";

interface AuthFormProps {
  isSignUp: boolean;
  isLoading: boolean;
  onSignIn: (email: string, password: string, turnstileToken: string, fingerprint?: string) => Promise<void>;
  onSignUp: (formData: {
    email: string;
    password: string;
    fullName: string;
    securityQuestion: string;
    securityAnswer: string;
    turnstileToken: string;
    fingerprint: string;
    referralCode?: string;
  }) => Promise<void>;
  onResetPassword: () => void;
  setIsSignUp: (value: boolean) => void;
}

export function AuthForm({
  isSignUp,
  isLoading,
  onSignIn,
  onSignUp,
  onResetPassword,
  setIsSignUp
}: AuthFormProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    securityQuestion: '',
    securityAnswer: '',
    rememberMe: false,
    referralCode: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [passwordMismatch, setPasswordMismatch] = useState<boolean>(false);
  const [isCheckingBreaches, setIsCheckingBreaches] = useState(false);
  const [wasBreachDetected, setWasBreachDetected] = useState(false);

  // Check if there's a stored referral code on component mount
  useEffect(() => {
    const storedReferralCode = getStoredReferralCode();
    if (storedReferralCode) {
      console.log('Retrieved stored referral code:', storedReferralCode);
      setFormData(prev => ({ ...prev, referralCode: storedReferralCode }));
      
      // Log that we found a stored referral code
      toast.info("A referral code has been applied to your signup", {
        id: "referral-code-found",
        duration: 3000
      });
    }
  }, []);

  // Check password validation on change when in signup mode
  useEffect(() => {
    if (isSignUp && formData.password) {
      const message = validatePassword(formData.password);
      setValidationMessage(message);
    } else {
      setValidationMessage(null);
    }
  }, [formData.password, isSignUp]);

  // Check if passwords match in signup mode
  useEffect(() => {
    if (isSignUp && formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setPasswordMismatch(true);
    } else {
      setPasswordMismatch(false);
    }
  }, [formData.password, formData.confirmPassword, isSignUp]);

  // Check for password breaches when password is valid
  useEffect(() => {
    const checkBreaches = async () => {
      if (isSignUp && formData.password && !validationMessage) {
        setIsCheckingBreaches(true);
        try {
          const isBreached = await checkPasswordBreached(formData.password);
          setWasBreachDetected(isBreached);
          if (isBreached) {
            toast.error("This password appears in known data breaches. Please choose a different password.");
          }
        } catch (error) {
          console.error("Error checking for breaches:", error);
        } finally {
          setIsCheckingBreaches(false);
        }
      }
    };

    // Debounce the breach check to avoid too many API calls
    const timer = setTimeout(checkBreaches, 800);
    return () => clearTimeout(timer);
  }, [isSignUp, formData.password, validationMessage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSecurityQuestionChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      securityQuestion: value
    }));
  };

  const validateForm = () => {
    if (isSignUp) {
      if (!formData.email) {
        toast.error("Please enter your email address");
        return false;
      }
      if (!formData.password) {
        toast.error("Please enter a password");
        return false;
      }
      if (validationMessage) {
        toast.error(validationMessage);
        return false;
      }
      if (wasBreachDetected) {
        toast.error("Please choose a different password that hasn't been exposed in data breaches");
        return false;
      }
      if (!formData.confirmPassword) {
        toast.error("Please confirm your password");
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error("Passwords do not match");
        return false;
      }
      if (!formData.fullName) {
        toast.error("Please enter your full name");
        return false;
      }
      if (!formData.securityQuestion) {
        toast.error("Please select a security question");
        return false;
      }
      if (!formData.securityAnswer) {
        toast.error("Please provide an answer to the security question");
        return false;
      }
      
      // Validate referral code if provided but not required
      if (formData.referralCode && !/^[a-zA-Z0-9]{4,20}$/.test(formData.referralCode)) {
        toast.error("Invalid referral code format");
        return false;
      }
      
      return true;
    }
    
    // Login validation
    if (!formData.email) {
      toast.error("Please enter your email address");
      return false;
    }
    if (!formData.password) {
      toast.error("Please enter your password");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Get device fingerprint
    const fingerprint = getDeviceFingerprint();
    
    // Check rate limits before proceeding
    const endpoint = isSignUp ? 'auth_signup' : 'auth_signin';
    const isAllowed = await checkRateLimit(endpoint);
    
    if (!isAllowed) {
      return; // Rate limited - message already shown by checkRateLimit
    }
    
    // Use empty string for turnstile token since we've disabled it
    const emptyToken = "";
    
    if (isSignUp) {
      try {
        // If there's a referral code, validate it before signup
        if (formData.referralCode) {
          console.log('Validating referral code before signup:', formData.referralCode);
          const isValidCode = await validateReferralCode(formData.referralCode);
          
          if (!isValidCode) {
            console.error('Invalid referral code during signup:', formData.referralCode);
            toast.error("The referral code you entered is invalid or expired");
            return;
          } else {
            console.log('Referral code valid, proceeding with signup');
          }
        }

        // Proceed with signup
        await onSignUp({
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
          securityQuestion: formData.securityQuestion,
          securityAnswer: formData.securityAnswer,
          turnstileToken: emptyToken,
          fingerprint,
          referralCode: formData.referralCode // Include referral code in signup
        });
      } catch (error) {
        console.error('Error during signup:', error);
        toast.error("Failed to create account. Please try again.");
      }
    } else {
      await onSignIn(formData.email, formData.password, emptyToken, fingerprint);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {isSignUp && (
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name</Label>
          <Input
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            type="text"
            placeholder="Enter your full name"
            required
            className="w-full"
            disabled={isLoading}
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          type="email"
          placeholder="Enter your email"
          required
          className="w-full"
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            required
            className="w-full pr-10"
            disabled={isLoading}
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
          </button>
        </div>
        
        {isSignUp && (
          <PasswordStrengthMeter 
            password={formData.password}
            validationMessage={validationMessage || (wasBreachDetected ? "Password appears in known data breaches" : null)}
            isCheckingBreaches={isCheckingBreaches}
          />
        )}
      </div>

      {isSignUp && (
        <>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                type={showPassword ? "text" : "password"}
                placeholder="Confirm your password"
                required
                className="w-full pr-10"
                disabled={isLoading}
              />
            </div>
            {passwordMismatch && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>

          {/* Referral code field */}
          <div className="space-y-2">
            <Label htmlFor="referralCode" className="flex items-center justify-between">
              <span>Referral Code</span>
              <span className="text-xs text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="referralCode"
              name="referralCode"
              value={formData.referralCode}
              onChange={handleChange}
              type="text"
              placeholder="Enter referral code if you have one"
              className="w-full"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground flex items-center">
              {formData.referralCode ? (
                <span className="text-blue-600">Referral code applied</span>
              ) : (
                "Enter a referral code if someone invited you"
              )}
            </p>
          </div>

          <SecurityQuestions
            securityQuestion={formData.securityQuestion}
            setSecurityQuestion={handleSecurityQuestionChange}
            securityAnswer={formData.securityAnswer}
            setSecurityAnswer={(value) => setFormData(prev => ({ ...prev, securityAnswer: value }))}
            isLoading={isLoading}
          />
        </>
      )}

      {!isSignUp && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="rememberMe"
              name="rememberMe"
              checked={formData.rememberMe}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, rememberMe: checked as boolean }))}
            />
            <label
              htmlFor="rememberMe"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Remember me
            </label>
          </div>
          <button
            type="button"
            onClick={onResetPassword}
            className="text-sm text-primary hover:underline"
          >
            Forgot password?
          </button>
        </div>
      )}

      <div className="flex gap-4">
        {!isSignUp ? (
          <>
            <Button 
              type="submit" 
              className="flex-1 bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] hover:opacity-90 transition-opacity"
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
            <Button 
              type="button" 
              onClick={() => setIsSignUp(true)}
              className="flex-1 bg-gradient-to-r from-[#D946EF] to-[#0EA5E9] hover:opacity-90 transition-opacity"
              disabled={isLoading}
            >
              Create Account
            </Button>
          </>
        ) : (
          <>
            <Button 
              type="submit" 
              className="flex-1 bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] hover:opacity-90 transition-opacity"
              disabled={isLoading || !!validationMessage || passwordMismatch || wasBreachDetected || isCheckingBreaches}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (isCheckingBreaches ? "Checking..." : "Sign up")}
            </Button>
            <Button 
              type="button"
              onClick={() => setIsSignUp(false)}
              className="flex-1 bg-gradient-to-r from-[#D946EF] to-[#0EA5E9] hover:opacity-90 transition-opacity"
              disabled={isLoading}
            >
              Back to Login
            </Button>
          </>
        )}
      </div>
    </form>
  );
}
